import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { usePrefersReducedMotion } from '@hooks';
import { loaderDelay, navDelay } from '@utils';
import TextTransition, { presets } from 'react-text-transition';

const StyledHeroSection = styled.section`
  ${({ theme }) => theme.mixins.flexCenter};
  flex-direction: column;
  min-height: 100vh;
  position: relative;
  text-align: center;
  padding: 0 20px;
  margin-top: clamp(-160px, -15vh, -60px);

  @media (max-height: 768px) {
    margin-top: clamp(0px, calc(144px - 20vh), 40px) !important;
  }

  @media (max-width: 768px) {
    margin-top: -40px !important;
    margin-bottom: 40px !important;
    padding: 0 !important;
  }
  @media (max-width: 480px) {
    margin-top: -60px !important;
    margin-bottom: 60px !important;
    padding: 0 !important;
  }

  @media (max-width: 360px) {
    margin-top: 0 !important;
    padding: 60px 0 120px !important;
    justify-content: flex-start !important;
  }

  @media (max-height: 500px) and (orientation: landscape) {
    margin-top: 0 !important;
    padding: 60px 0 80px !important;
    min-height: auto;
    justify-content: flex-start;
  }

  > div {
    width: 100%;
    display: flex;
    justify-content: center;
  }

  .hero-card {
    background: rgba(255, 255, 255, 0.03);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    padding: clamp(30px, 5vw, 60px) clamp(40px, 6vw, 60px);
    max-width: 800px;
    width: 100%;
    text-align: left;
    box-shadow: 0 10px 30px -15px rgba(0, 0, 0, 0.5);
    transform: translateY(-40px);

    @media (max-height: 768px) {
      padding: clamp(24px, 4vh, 40px) clamp(40px, 6vw, 60px);
      transform: translateY(0) !important;
    }

    @media (max-width: 768px) {
      width: 100% !important;
      max-width: 100% !important;
      padding: 24px 25px 24px 25px !important;
      border-radius: 16px !important;
      background: rgba(255, 255, 255, 0.03) !important;
      border: 1px solid rgba(255, 255, 255, 0.1) !important;
      -webkit-backdrop-filter: blur(8px) !important;
      backdrop-filter: blur(8px) !important;
      box-shadow: 0 10px 20px -10px rgba(0, 0, 0, 0.5) !important;
      transform: translateZ(0) !important;
      will-change: transform, backdrop-filter !important;
    }

    @media (max-height: 500px) and (orientation: landscape) {
      padding: 20px !important;
      margin-bottom: 20px !important;
    }
  }

  .overline {
    display: inline-block;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-md);
    font-weight: 500;
    margin-bottom: 15px;
    letter-spacing: 5px;
    -webkit-text-stroke: 0.5px var(--green);
    text-transform: uppercase;
    text-shadow: 0 0 15px rgba(255, 42, 42, 0.6);
    background: rgba(255, 42, 42, 0.1);
    padding: 4px 10px;
    border-radius: 4px;
    border: 1px solid rgba(255, 42, 42, 0.2);
    white-space: normal;
    word-break: break-all;

    &::after {
      content: '_';
      animation: blink 1s step-end infinite;
      margin-left: 5px;
      color: var(--green);
    }

    @media (max-width: 768px) {
      font-size: 13px !important;
      letter-spacing: 2.5px !important;
      margin-top: 0 !important;
      margin-bottom: 12px !important;
      position: relative;
      top: 0 !important;
      white-space: normal !important;
      word-break: break-all !important;
      padding: 5px 12px !important;
    }

    @media (max-width: 360px) {
      white-space: normal !important;
      word-break: break-all !important;
      font-size: 11px !important;
      letter-spacing: 1.5px !important;
    }
  }

  .big-heading {
    margin: 0;
    font-size: clamp(34px, 6.5vw, 80px);
    line-height: 1.1;
    font-weight: 700;
    letter-spacing: -1.5px;
    background: linear-gradient(90deg, #ff2a2a 0%, #ffffff 50%, #ff2a2a 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    font-family: 'Outfit', var(--font-sans);
    padding-bottom: 10px;
    white-space: nowrap;

    @media (max-height: 768px) {
      font-size: clamp(34px, 5.5vw, 64px) !important;
    }

    @media (max-width: 768px) {
      font-size: clamp(36px, 9vw, 48px) !important;
      letter-spacing: -1px;
      position: relative;
      top: 0 !important;
      padding-bottom: 5px !important;
      margin-bottom: 6px !important;
    }

    @media (max-width: 360px) {
      white-space: normal !important;
      font-size: 32px !important;
      word-break: keep-all;
    }
  }

  .subtitle-wrapper {
    margin-top: 10px;
    display: flex;
    justify-content: flex-start;
    align-items: center;
    font-size: clamp(22px, 5.5vw, 36px);
    font-weight: 600;
    letter-spacing: -0.5px;
    color: var(--lightest-slate);
    font-family: 'Space Grotesk', var(--font-sans);
    line-height: 1.4;
    word-break: break-word;

    @media (max-height: 768px) {
      font-size: clamp(18px, 3.5vw, 28px) !important;
    }

    .text-transition {
      width: 100% !important;
      overflow: visible !important;

      div {
        width: 100% !important;
        overflow: visible !important;
      }
    }

    * {
      line-height: 1.4 !important;
    }

    @media (max-width: 768px) {
      font-size: clamp(16px, 6vw, 28px) !important;
      font-weight: 800 !important;
      align-items: flex-start !important;
      position: relative;
      top: 0 !important;
      margin-top: 0 !important;
      margin-bottom: 15px !important;
      min-height: 65px !important;

      /* Force the rotating subtitle text to appear bold on mobile */
      .subtitle-text {
        font-weight: 800 !important;
        display: inline-block;
      }

      * {
        line-height: 1.4 !important;
      }
    }
  }

  p {
    margin: clamp(15px, 3vh, 30px) 0;
    max-width: 600px;
    color: var(--slate);
    font-size: var(--fz-lg);
    line-height: 1.7;
    font-weight: 400;

    @media (max-height: 768px) {
      font-size: var(--fz-lg) !important;
      margin: 12px 0 16px !important;
    }

    &:last-child,
    &:last-of-type {
      margin: clamp(15px, 3vh, 30px) 0;

      @media (max-width: 768px) {
        margin: 10px 0 20px !important;
      }
    }

    @media (max-width: 768px) {
      margin: 12px 0 20px !important;
      font-size: 15px !important;
      line-height: 1.5 !important;
      word-spacing: 0.05em !important;
    }
  }

  .button-group {
    display: flex;
    justify-content: flex-start;
    gap: 15px;
    flex-wrap: wrap;

    a {
      display: inline-block;
      padding: 14px 28px;
      font-size: var(--fz-sm);
      font-family: var(--font-mono);
      font-weight: 500;
      letter-spacing: 0.5px;
      border-radius: 4px;
      text-decoration: none;
      transition: var(--transition);
      text-align: center;
      min-width: 160px;
    }

    .primary {
      background: var(--green);
      color: var(--white);
      border: 1px solid var(--green);
      box-shadow: 0 0 10px rgba(255, 42, 42, 0.3);

      @media (hover: hover) {
        &:hover {
          background: transparent;
          color: var(--green);
          box-shadow: 0 0 20px rgba(255, 42, 42, 0.5);
          transform: translateY(-3px) scale(1.03);
        }
      }

      &:active {
        background: transparent;
        color: var(--green);
        box-shadow: 0 0 20px rgba(255, 42, 42, 0.5);
        transform: translateY(-3px) scale(1.03);
      }
    }

    .secondary {
      background: rgba(255, 255, 255, 0.02);
      color: var(--white);
      border: 1px solid rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(5px);

      @media (hover: hover) {
        &:hover {
          background: rgba(255, 255, 255, 0.05);
          border-color: var(--green);
          color: var(--green);
          transform: translateY(-3px) scale(1.03);
        }
      }

      &:active {
        background: rgba(255, 255, 255, 0.05);
        border-color: var(--green);
        color: var(--green);
        transform: translateY(-3px) scale(1.03);
      }
    }

    @media (max-width: 768px) {
      a {
        padding: 12px 20px !important;
        font-size: 13px !important;
      }
    }

    @media (max-width: 480px) {
      flex-direction: column;
      width: 100%;
      gap: 10px;

      a {
        width: 100%;
        min-width: unset;
      }
    }
  }

  .meeting-box {
    margin-top: 25px;
    padding: 8px 16px;
    background: rgba(255, 42, 42, 0.05);
    border: 1px solid rgba(255, 42, 42, 0.3);
    border-radius: 50px;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: var(--fz-xs);
    font-family: var(--font-mono);

    .dot {
      width: 6px;
      height: 6px;
      background-color: #ff2a2a;
      border-radius: 50%;
      display: inline-block;
      box-shadow: 0 0 5px #ff2a2a;
    }

    span {
      color: var(--light-slate);
    }

    a {
      color: #ff2a2a;
      font-weight: 600;
      text-decoration: none;
      transition: var(--transition);

      @media (hover: hover) {
        &:hover,
        &:focus-visible {
          color: #fff;
          text-shadow: 0 0 5px rgba(255, 42, 42, 0.5);
        }
      }
      &:active {
        color: #fff;
        text-shadow: 0 0 5px rgba(255, 42, 42, 0.5);
      }
    }

    @media (max-width: 768px) {
      padding: 8px 12px !important;
      font-size: 11px !important;
      gap: 6px !important;
      flex-direction: row !important;
      flex-wrap: wrap;
      justify-content: center;
      border-radius: 10px !important;
      margin-top: 15px !important;
    }

    @media (max-width: 480px) {
      .dot {
        display: none;
      }
    }
  }

  @keyframes blink {
    from,
    to {
      opacity: 1;
    }
    50% {
      opacity: 0;
    }
  }
`;

const TEXTS = [
  'Software Engineer',
  'Business Central Consultant',
  'Full Stack Web Developer',
  'AI Engineer',
  'DevOps Engineer',
  'Flutter Developer',
  'Tech Enthusiast',
];

const Hero = () => {
  const [index, setIndex] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    const handleResize = () => {
      setIsMobileDevice(window.innerWidth <= 768);
    };
    if (typeof window !== 'undefined') {
      handleResize();
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined' && document.fonts) {
      document.fonts.ready.then(() => {
        setFontsLoaded(true);
      });
    }
  }, []);

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }
    const timeout = setTimeout(() => setIsMounted(true), isMobileDevice ? 300 : navDelay);
    return () => clearTimeout(timeout);
  }, [prefersReducedMotion, isMobileDevice]);

  useEffect(() => {
    const intervalId = setInterval(() => setIndex(index => index + 1), 1500);
    return () => clearInterval(intervalId);
  }, []);

  const content = (
    <div className="hero-card">
      <span className="overline">[ SYSTEM.INIT ]</span>
      <h1 className="big-heading">Noor Ul Baseer</h1>

      <div className="subtitle-wrapper">
        <span className="subtitle-text">
          <TextTransition springConfig={presets.gentle} key={fontsLoaded ? 'loaded' : 'loading'}>
            {TEXTS[index % TEXTS.length]}
          </TextTransition>
        </span>
      </div>

      <p>
        I craft intelligent, user-centric digital experiences. From building scalable full-stack
        applications to engineering AI solutions, I turn complex problems into elegant, modern
        software.
      </p>

      <div className="button-group">
        <a href="#contact" className="primary">
          Get In Touch
        </a>
        <a
          href="/Noor-Ul-Baseer's_Resume.pdf"
          target="_blank"
          rel="noreferrer"
          className="secondary"
        >
          View Resume
        </a>
      </div>

      <div style={{ height: '10px' }}></div>

      <div className="meeting-box">
        <span className="dot"></span>
        <span>Let's build something amazing! 🚀</span>
        <a
          href="https://cal.com/noorulbaseer/15min"
          target="_blank"
          rel="noreferrer"
          onClick={e => e.currentTarget.blur()}
        >
          Schedule a Chat →
        </a>
      </div>
    </div>
  );

  return (
    <StyledHeroSection>
      {prefersReducedMotion ? (
        <div>{content}</div>
      ) : (
        <TransitionGroup component={null}>
          {isMounted && (
            <CSSTransition classNames="fadeup" timeout={loaderDelay}>
              <div style={{ transitionDelay: isMobileDevice ? '200ms' : '100ms' }}>{content}</div>
            </CSSTransition>
          )}
        </TransitionGroup>
      )}
    </StyledHeroSection>
  );
};

export default Hero;
